/** Parts of the top bar that can leave when there is not enough room. */
export type PieceId =
  | 'hourly'
  | 'cities'
  | 'extras'
  | 'btc'
  | 'eur'
  | 'gold'
  | 'rain'
  | 'wind'
  | 'usd'
  | 'netWorth'
  | 'date'
  | 'location'
  | 'now';

/**
 * Least important first. The extra cities go before the primary city's wind and precipitation, the
 * date after the widgets, and the primary weather essentials (location, then temperature) leave last.
 * The clock, bell and settings gear never leave.
 */
export const DROP_ORDER: readonly PieceId[] = [
  'hourly',
  'cities',
  'extras',
  'btc',
  'eur',
  'gold',
  'rain',
  'wind',
  'usd',
  'netWorth',
  'date',
  'location',
  'now',
];

/** Widths in px of the parts that are always drawn, measured against the real bar. */
export const CHROME = {
  /** Clock time, settings, and the bar's own padding and gaps. */
  core: 150,
  bell: 34,
  date: 130,
  /** The extra width of `:ss` on the clock. */
  seconds: 26,
} as const;

/**
 * Which parts to hide so that `fixed` plus every remaining part fits in `available`.
 * Parts are removed strictly in `DROP_ORDER`, so a wider bar always shows a superset of a narrower one.
 * A part missing from `widths` (not present at all) is skipped and costs nothing.
 */
export function selectHidden(available: number, widths: Partial<Record<PieceId, number>>, fixed: number): Set<PieceId> {
  let total = fixed + DROP_ORDER.reduce((sum, id) => sum + (widths[id] ?? 0), 0);
  const hidden = new Set<PieceId>();
  for (const id of DROP_ORDER) {
    if (total <= available) break;
    const width = widths[id] ?? 0;
    if (width <= 0) continue;
    hidden.add(id);
    total -= width;
  }
  return hidden;
}
